import React from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft } from "lucide-react";
import "./App.css";

const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 rounded-md",
    lg: "h-11 px-8 rounded-md",
  };

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
};

function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 md:px-12 border-b border-gray-200">
        <Link to="/" className="flex items-center space-x-2">
          <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-medium text-black">Sana</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </Link>
      </nav>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <h1 className="text-4xl md:text-5xl font-medium text-black mb-4">
          Privacy Policy
        </h1>
        <p className="text-sm text-black opacity-60 mb-8">
          Last Updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>

        <div className="prose prose-lg max-w-none text-black">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg leading-relaxed opacity-90 mb-6">
              At Sana ("we," "our," or "us"), we are committed to protecting
              your privacy and ensuring the security of your health data. This
              Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our mobile application,
              website, and related services (collectively, the "Service").
            </p>
            <p className="text-lg leading-relaxed opacity-90">
              <strong className="text-black">IMPORTANT:</strong> Please read
              this Privacy Policy carefully. By using the Service, you agree to
              the collection and use of information in accordance with this
              policy. If you do not agree with our policies and practices, do
              not use the Service.
            </p>
          </section>

          {/* Anonymous Sessions */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              1. Anonymous Sessions and No Personal Identification
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  Sana uses anonymous sessions and does not collect personally
                  identifiable information (PII) about you.
                </strong>{" "}
                We do not require you to create an account, provide your name,
                email address, phone number, or any other information that could
                be used to identify you personally.
              </p>
              <p>
                Your use of the Service is tracked through anonymous session
                identifiers that are not linked to your identity. These session
                identifiers allow us to provide you with a personalized
                experience within a single session but do not allow us to
                identify you across different sessions or devices.
              </p>
              <p>We do not collect, store, or process information such as:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your name or username</li>
                <li>Email addresses or phone numbers</li>
                <li>Physical addresses or location data that identifies you</li>
                <li>IP addresses (or we anonymize them immediately)</li>
                <li>Device identifiers that can be linked to you</li>
                <li>Social media accounts or profiles</li>
                <li>
                  Any other information that could be used to identify you
                </li>
              </ul>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              2. Information We Collect
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                While we do not collect personally identifiable information, we
                may collect and process the following types of anonymous data:
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                2.1 Health and Wellness Data
              </h3>
              <p>
                You may choose to input health-related information into the
                Service, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Biometric data (heart rate variability, sleep data, activity
                  levels, body measurements)
                </li>
                <li>Protocol adherence and tracking information</li>
                <li>Health goals and progress metrics</li>
                <li>Dietary and lifestyle information</li>
                <li>Wellness notes and observations</li>
                <li>Protocol preferences and customizations</li>
              </ul>
              <p className="mt-4">
                <strong className="text-black">Important:</strong> This health
                data is stored locally on your device and/or in anonymous
                sessions that cannot be linked to your identity. We cannot
                access this data in a way that identifies you personally.
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                2.2 Usage Data
              </h3>
              <p>
                We may collect anonymous information about how you interact with
                the Service, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Features you access and use</li>
                <li>Time spent on different sections of the Service</li>
                <li>Protocols you view or interact with</li>
                <li>General usage patterns and preferences</li>
                <li>
                  Technical information about your device (device type, OS
                  version, screen size) in anonymized form
                </li>
              </ul>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                2.3 Technical Data
              </h3>
              <p>
                We may automatically collect certain technical information that
                does not identify you personally, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Anonymized or aggregated analytics data</li>
                <li>
                  Error logs and crash reports (without personal identifiers)
                </li>
                <li>Performance metrics</li>
                <li>Browser or app version information</li>
              </ul>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              3. How We Use Your Information
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                We use the anonymous information we collect for the following
                purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-black">
                    To Provide the Service:
                  </strong>{" "}
                  To deliver, maintain, and improve the functionality of the
                  Service, including protocol scheduling, progress tracking, and
                  personalized recommendations within your session
                </li>
                <li>
                  <strong className="text-black">
                    To Improve the Service:
                  </strong>{" "}
                  To analyze usage patterns and trends in anonymized, aggregated
                  form to enhance user experience and develop new features
                </li>
                <li>
                  <strong className="text-black">
                    Research and Analytics:
                  </strong>{" "}
                  To conduct research and analysis on anonymized, aggregated
                  data to better understand health trends and protocol
                  effectiveness
                </li>
                <li>
                  <strong className="text-black">Technical Support:</strong> To
                  diagnose technical issues, prevent errors, and ensure the
                  security and stability of the Service
                </li>
                <li>
                  <strong className="text-black">Legal Compliance:</strong> To
                  comply with applicable laws, regulations, and legal processes
                </li>
                <li>
                  <strong className="text-black">Security:</strong> To detect,
                  prevent, and address security issues, fraud, or other
                  potentially harmful activities
                </li>
              </ul>
              <p className="mt-4">
                <strong className="text-black">We do not:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use your data to identify you personally</li>
                <li>
                  Share your data with third parties for marketing purposes
                </li>
                <li>Sell your data to third parties</li>
                <li>Use your data to create profiles about you</li>
                <li>Link your health data to your identity</li>
              </ul>
            </div>
          </section>

          {/* Data Storage and Security */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              4. Data Storage and Security
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">Local Storage:</strong> Much of
                your health data and preferences are stored locally on your
                device. This means you have direct control over this data, and
                it is not transmitted to our servers unless you choose to sync
                it (and even then, it remains anonymous).
              </p>
              <p>
                <strong className="text-black">Anonymous Session Data:</strong>{" "}
                Data stored in anonymous sessions is encrypted and cannot be
                linked to your identity. Session data is automatically deleted
                after a period of inactivity or when you clear your session.
              </p>
              <p>
                <strong className="text-black">Security Measures:</strong> We
                implement industry-standard security measures to protect the
                data we collect, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure data transmission protocols (HTTPS/TLS)</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication for our systems</li>
                <li>Anonymization and aggregation of data where possible</li>
                <li>Regular deletion of old session data</li>
              </ul>
              <p>
                However, no method of transmission over the internet or
                electronic storage is 100% secure. While we strive to use
                commercially acceptable means to protect your data, we cannot
                guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Data Sharing and Disclosure */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              5. Data Sharing and Disclosure
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                We do not sell, trade, or rent your personal information to
                third parties. We may share anonymized, aggregated data in the
                following circumstances:
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                5.1 Service Providers
              </h3>
              <p>
                We may share anonymized data with trusted third-party service
                providers who assist us in operating the Service, conducting
                business, or serving users. These service providers are
                contractually obligated to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the data only for the purposes we specify</li>
                <li>Maintain the anonymity of the data</li>
                <li>Implement appropriate security measures</li>
                <li>Not attempt to re-identify individuals</li>
              </ul>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                5.2 Research and Analytics
              </h3>
              <p>
                We may share anonymized, aggregated health data with research
                institutions, academic organizations, or analytics providers for
                the purpose of advancing health research and improving protocol
                effectiveness. This data cannot be used to identify you.
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                5.3 Legal Requirements
              </h3>
              <p>
                We may disclose information if required to do so by law or in
                response to valid requests by public authorities (e.g., court
                orders, government agencies). However, since we do not collect
                personally identifiable information, any such disclosure would
                be limited to anonymous, aggregated data.
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                5.4 Business Transfers
              </h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your
                anonymous data may be transferred. However, the data will remain
                anonymous and subject to the same privacy protections.
              </p>
            </div>
          </section>

          {/* Your Rights and Choices */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              6. Your Rights and Choices
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                Even though we do not collect personally identifiable
                information, you have the following rights regarding your data:
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                6.1 Access and Control
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-black">Local Data:</strong> You can
                  access, modify, or delete data stored locally on your device
                  at any time through the Service settings
                </li>
                <li>
                  <strong className="text-black">Session Data:</strong> You can
                  clear your session data at any time, which will delete all
                  anonymous session information
                </li>
                <li>
                  <strong className="text-black">Opt-Out:</strong> You can
                  choose not to provide certain types of data, though this may
                  limit some features of the Service
                </li>
              </ul>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                6.2 Data Deletion
              </h3>
              <p>
                You can delete your local data and clear your session at any
                time. Since we do not collect personally identifiable
                information, we cannot identify and delete specific data
                associated with you across sessions. However, anonymous session
                data is automatically deleted after periods of inactivity.
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                6.3 Do Not Track
              </h3>
              <p>
                Some browsers and devices offer "Do Not Track" settings. While
                we respect your privacy preferences, we do not currently respond
                to Do Not Track signals because we do not track you across
                sessions or devices in an identifiable way.
              </p>

              <h3 className="text-xl font-medium text-black mt-6 mb-3">
                6.4 Regional Privacy Rights
              </h3>
              <p>
                Depending on your location, you may have additional rights under
                applicable privacy laws (such as GDPR, CCPA, etc.). Since we do
                not collect personally identifiable information, many of these
                rights may not be directly applicable. However, we are committed
                to respecting your privacy rights to the extent possible.
              </p>
              <p>
                If you have questions about your privacy rights, please contact
                us using the information provided in the Contact section below.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              7. Children's Privacy
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                The Service is not intended for children under the age of 18. We
                do not knowingly collect information from children under 18. If
                we become aware that we have collected information from a child
                under 18, we will take steps to delete such information
                promptly.
              </p>
              <p>
                If you are a parent or guardian and believe your child has
                provided us with information, please contact us immediately so
                we can address the situation. However, since we use anonymous
                sessions and do not collect identifying information, we may not
                be able to identify specific data to delete.
              </p>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              8. Third-Party Services and Links
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                The Service may contain links to third-party websites, services,
                or applications. We are not responsible for the privacy
                practices of these third parties. We encourage you to review the
                privacy policies of any third-party services you access.
              </p>
              <p>
                The Service may integrate with third-party services (such as
                health tracking devices, wearables, or other apps). When you
                choose to connect these services, they may collect and share
                data according to their own privacy policies. We are not
                responsible for the privacy practices of these third-party
                services.
              </p>
            </div>
          </section>

          {/* International Data Transfers */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              9. International Data Transfers
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                Your information may be transferred to and processed in
                countries other than your country of residence. These countries
                may have data protection laws that differ from those in your
                country.
              </p>
              <p>
                However, since we use anonymous sessions and do not collect
                personally identifiable information, the risk of harm from such
                transfers is minimized. We take appropriate safeguards to ensure
                that your anonymous data receives an adequate level of
                protection regardless of where it is processed.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              10. Data Retention
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">Local Data:</strong> Data stored
                locally on your device will be retained until you delete it or
                uninstall the Service.
              </p>
              <p>
                <strong className="text-black">Session Data:</strong> Anonymous
                session data is automatically deleted after a period of
                inactivity (typically 30-90 days) or when you clear your
                session.
              </p>
              <p>
                <strong className="text-black">Aggregated Data:</strong> We may
                retain anonymized, aggregated data indefinitely for research and
                analytics purposes, as this data cannot be used to identify you.
              </p>
              <p>
                We will delete or anonymize your data when it is no longer
                necessary for the purposes outlined in this Privacy Policy,
                unless we are required to retain it for legal or regulatory
                purposes.
              </p>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              11. Changes to This Privacy Policy
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or for
                other reasons. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Posting the updated Privacy Policy on this page</li>
                <li>
                  Updating the "Last Updated" date at the top of this policy
                </li>
                <li>
                  Providing notice through the Service if the changes are
                  significant
                </li>
              </ul>
              <p>
                Your continued use of the Service after any changes to this
                Privacy Policy constitutes your acceptance of the updated
                policy. We encourage you to review this Privacy Policy
                periodically to stay informed about how we protect your
                information.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              12. Contact Us
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mt-4">
                <p className="font-medium mb-2">Sana Privacy Team</p>
                <p>Email: privacy@sana.app</p>
                <p className="mt-2 text-sm opacity-70 italic">
                  Note: Add your physical business address here if required by
                  applicable law (e.g., GDPR, CCPA).
                </p>
              </div>
              <p className="mt-4">
                We will respond to your inquiry within a reasonable timeframe
                and in accordance with applicable privacy laws.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="mb-12 mt-16 p-8 bg-gray-50 rounded-lg border-2 border-black">
            <p className="text-lg font-medium text-black mb-4">
              Your Privacy Matters
            </p>
            <p className="text-black opacity-90 leading-relaxed">
              We are committed to protecting your privacy and maintaining the
              anonymity of your data. By using the Service, you acknowledge that
              you have read and understood this Privacy Policy and agree to our
              collection and use of information as described herein. If you do
              not agree with this Privacy Policy, please do not use the Service.
            </p>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-medium text-black">Sana</span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-black opacity-70">
              <Link
                to="/privacy"
                className="hover:opacity-100 transition-opacity"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:opacity-100 transition-opacity"
              >
                Terms
              </Link>
              <Link
                to="/support"
                className="hover:opacity-100 transition-opacity"
              >
                Support
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-black opacity-50">
              © 2024 Sana. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Privacy;

